import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { speakText } from '../utils/speak';
import './VerbDetail.css';

const tenseLabelsByLanguage = {
    Italian: {
        presente: 'Presente', passato_prossimo: 'Passato Prossimo', imperfetto: 'Imperfetto',
        trapassato_prossimo: 'Trapassato Prossimo', passato_remoto: 'Passato Remoto', trapassato_remoto: 'Trapassato Remoto',
        futuro_semplice: 'Futuro Semplice', futuro_anteriore: 'Futuro Anteriore',
        condizionale_presente: 'Condizionale Presente', condizionale_passato: 'Condizionale Passato',
        congiuntivo_presente: 'Congiuntivo Presente', congiuntivo_passato: 'Congiuntivo Passato',
        congiuntivo_imperfetto: 'Congiuntivo Imperfetto', congiuntivo_trapassato: 'Congiuntivo Trapassato',
        imperativo: 'Imperativo',
    },
    English: {
        simple_present: 'Simple Present', present_continuous: 'Present Continuous',
        simple_past: 'Simple Past', past_continuous: 'Past Continuous',
        present_perfect: 'Present Perfect', present_perfect_continuous: 'Present Perfect Continuous',
        past_perfect: 'Past Perfect', past_perfect_continuous: 'Past Perfect Continuous',
        simple_future: 'Simple Future', future_continuous: 'Future Continuous', future_perfect: 'Future Perfect',
        conditional_present: 'Conditional Present', conditional_perfect: 'Conditional Perfect', imperative: 'Imperative',
    },
    Spanish: {
        presente: 'Presente', preterito_perfecto: 'Pretérito Perfecto', preterito_indefinido: 'Pretérito Indefinido',
        preterito_imperfecto: 'Pretérito Imperfecto', pluscuamperfecto: 'Pluscuamperfecto',
        futuro_simple: 'Futuro Simple', futuro_perfecto: 'Futuro Perfecto',
        condicional_simple: 'Condicional Simple', condicional_perfecto: 'Condicional Perfecto',
        presente_subjuntivo: 'Presente Subjuntivo', preterito_perfecto_subjuntivo: 'Pretérito Perfecto Subjuntivo',
        preterito_imperfecto_subjuntivo: 'Pretérito Imperfecto Subjuntivo', imperativo: 'Imperativo',
    },
    French: {
        present: 'Présent', passe_compose: 'Passé Composé', imparfait: 'Imparfait',
        plus_que_parfait: 'Plus-que-parfait', passe_simple: 'Passé Simple',
        futur_simple: 'Futur Simple', futur_anterieur: 'Futur Antérieur',
        conditionnel_present: 'Conditionnel Présent', conditionnel_passe: 'Conditionnel Passé',
        subjonctif_present: 'Subjonctif Présent', subjonctif_passe: 'Subjonctif Passé',
        subjonctif_imparfait: 'Subjonctif Imparfait', imperatif: 'Impératif',
    },
    Portuguese: {
        presente: 'Presente', preterito_perfeito: 'Pretérito Perfeito', preterito_imperfeito: 'Pretérito Imperfeito',
        mais_que_perfeito: 'Mais-que-perfeito', futuro_simples: 'Futuro Simples', futuro_perfeito: 'Futuro Perfeito',
        condicional_simples: 'Condicional Simples', condicional_perfeito: 'Condicional Perfeito',
        presente_subjuntivo: 'Presente do Subjuntivo', preterito_perfeito_subjuntivo: 'Pretérito Perfeito do Subjuntivo',
        imperfeito_subjuntivo: 'Imperfeito do Subjuntivo', imperativo: 'Imperativo',
    },
};

const tenseOrderByLanguage = {
    Italian: ['presente', 'passato_prossimo', 'imperfetto', 'trapassato_prossimo', 'passato_remoto', 'trapassato_remoto', 'futuro_semplice', 'futuro_anteriore', 'condizionale_presente', 'condizionale_passato', 'congiuntivo_presente', 'congiuntivo_passato', 'congiuntivo_imperfetto', 'congiuntivo_trapassato', 'imperativo'],
    English: ['simple_present', 'present_continuous', 'simple_past', 'past_continuous', 'present_perfect', 'present_perfect_continuous', 'past_perfect', 'past_perfect_continuous', 'simple_future', 'future_continuous', 'future_perfect', 'conditional_present', 'conditional_perfect', 'imperative'],
    Spanish: ['presente', 'preterito_perfecto', 'preterito_indefinido', 'preterito_imperfecto', 'pluscuamperfecto', 'futuro_simple', 'futuro_perfecto', 'condicional_simple', 'condicional_perfecto', 'presente_subjuntivo', 'preterito_perfecto_subjuntivo', 'preterito_imperfecto_subjuntivo', 'imperativo'],
    French: ['present', 'passe_compose', 'imparfait', 'plus_que_parfait', 'passe_simple', 'futur_simple', 'futur_anterieur', 'conditionnel_present', 'conditionnel_passe', 'subjonctif_present', 'subjonctif_passe', 'subjonctif_imparfait', 'imperatif'],
    Portuguese: ['presente', 'preterito_perfeito', 'preterito_imperfeito', 'mais_que_perfeito', 'futuro_simples', 'futuro_perfeito', 'condicional_simples', 'condicional_perfeito', 'presente_subjuntivo', 'preterito_perfeito_subjuntivo', 'imperfeito_subjuntivo', 'imperativo'],
};

const personLabelsByLanguage = {
    Italian: { io: 'Io', tu: 'Tu', lui_lei: 'Lui, Lei', noi: 'Noi', voi: 'Voi', loro: 'Loro' },
    English: { i: 'I', you: 'You', he_she_it: 'He, She, It', we: 'We', you_plural: 'You (plural)', they: 'They' },
    Spanish: { yo: 'Yo', tu: 'Tú', el_ella_usted: 'Él, Ella, Usted', nosotros: 'Nosotros', vosotros: 'Vosotros', ellos_ustedes: 'Ellos, Ustedes' },
    French: { je: 'Je', tu: 'Tu', il_elle: 'Il, Elle', nous: 'Nous', vous: 'Vous', ils_elles: 'Ils, Elles' },
    Portuguese: { eu: 'Eu', tu: 'Tu', ele_ela_voce: 'Ele, Ela, Você', nos: 'Nós', vos: 'Vós', eles_elas_voces: 'Eles, Elas, Vocês' },
};

const personOrderByLanguage = {
    Italian: ['io', 'tu', 'lui_lei', 'noi', 'voi', 'loro'],
    English: ['i', 'you', 'he_she_it', 'we', 'you_plural', 'they'],
    Spanish: ['yo', 'tu', 'el_ella_usted', 'nosotros', 'vosotros', 'ellos_ustedes'],
    French: ['je', 'tu', 'il_elle', 'nous', 'vous', 'ils_elles'],
    Portuguese: ['eu', 'tu', 'ele_ela_voce', 'nos', 'vos', 'eles_elas_voces'],
};

function VerbDetail() {
    const navigate = useNavigate();
    const { verbSlug } = useParams();
    const typedVerb = decodeURIComponent(verbSlug || '');
    const targetLanguageRaw = localStorage.getItem('vozoly_target_language') || 'italian';
    const targetLanguage = targetLanguageRaw.charAt(0).toUpperCase() + targetLanguageRaw.slice(1).toLowerCase();
    const tenseLabels = tenseLabelsByLanguage[targetLanguage] || tenseLabelsByLanguage.Italian;
    const tenseOrder = tenseOrderByLanguage[targetLanguage] || tenseOrderByLanguage.Italian;
    const personLabels = personLabelsByLanguage[targetLanguage] || personLabelsByLanguage.Italian;
    const personOrder = personOrderByLanguage[targetLanguage] || personOrderByLanguage.Italian;

    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [data, setData] = useState(null);
    const [expandedTense, setExpandedTense] = useState(tenseOrder[0]);
    const [saveTarget, setSaveTarget] = useState(null);
    const [savedFeedback, setSavedFeedback] = useState('');

    useEffect(() => {
        const fetchConjugation = async () => {
            setLoading(true);
            setNotFound(false);
            try {
                const interfaceLangRaw = localStorage.getItem('vozoly_interface_language') || 'en';
                const interfaceLangNames = { en: 'English', it: 'Italian', es: 'Spanish', fr: 'French', pt: 'Portuguese' };
                const interfaceLanguage = interfaceLangNames[interfaceLangRaw] || 'English';
                const response = await fetch('/api/get-conjugation', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ verb: typedVerb, language: targetLanguage, interfaceLanguage }),
                });
                if (!response.ok) throw new Error('API unavailable');
                const result = await response.json();
                if (!result.valid) {
                    setNotFound(true);
                } else {
                    setData(result);
                }
            } catch (err) {
                setNotFound(true);
            }
            setLoading(false);
        };
        fetchConjugation();
    }, [typedVerb, targetLanguage]);

    const handleSaveInfinitive = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !data) return;
        await supabase.from('word_bank').upsert({
            user_id: user.id,
            word: data.infinitive,
            translation: data.translation,
            note: `Base verb (${data.conjugationGroup || 'verb'}).`,
            type: 'verb',
            source: 'grammar',
            entry_type: 'word',
            target_language: targetLanguageRaw.toLowerCase(),
        }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: true });
        setSavedFeedback(data.infinitive);
        setTimeout(() => setSavedFeedback(''), 1500);
    };

    const handleSave = async (saveBase) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !saveTarget) return;

        const wordToSave = saveBase ? data.infinitive : saveTarget.form;
        const entryType = wordToSave.trim().split(/\s+/).length > 1 ? 'phrase' : 'word';
        const note = saveBase
            ? `Base verb (${data.conjugationGroup || 'verb'}).`
            : `${tenseLabels[saveTarget.tense]} form, ${personLabels[saveTarget.person] || saveTarget.person} — from the verb "${data.infinitive}".`;

        await supabase.from('word_bank').upsert({
            user_id: user.id,
            word: wordToSave,
            translation: data.translation,
            note,
            type: 'verb',
            source: 'grammar',
            entry_type: entryType,
            target_language: targetLanguageRaw.toLowerCase(),
        }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: true });

        setSaveTarget(null);
        setSavedFeedback(wordToSave);
        setTimeout(() => setSavedFeedback(''), 1500);
    };

    return (
        <div className="vd-screen">

            <div className="vd-top-bar">
                <button className="vd-back" onClick={() => navigate(-1)}>←</button>
                <div className="vd-top-title">{loading ? 'Loading...' : (data?.infinitive || typedVerb)}</div>
                <div style={{ width: 32 }}></div>
            </div>

            {loading && (
                <div className="vd-state">
                    <div className="vd-state-icon">⏳</div>
                    <div className="vd-state-text">Looking up "{typedVerb}"...</div>
                </div>
            )}

            {!loading && notFound && (
                <div className="vd-state">
                    <div className="vd-state-icon">❓</div>
                    <div className="vd-state-text">
                        We couldn't load "{typedVerb}" right now. This could be a spelling issue or a temporary service outage. Check the spelling and try again, or come back shortly.
                    </div>
                    <button className="vd-back-btn" onClick={() => navigate('/grammar')}>Back to Grammar</button>
                </div>
            )}

            {!loading && !notFound && data && (
                <>
                    <div className="vd-hero">
                        <div className="vd-infinitive-row">
                            <div className="vd-infinitive">{data.infinitive}</div>
                            <button className="vd-speak-btn" onClick={() => speakText(data.infinitive, null, targetLanguageRaw)} title="Hear pronunciation">🔊</button>
                        </div>
                        <div className="vd-translation">{data.translation}</div>
                        <button className="vd-save-infinitive-btn" onClick={handleSaveInfinitive}>
                            🔖 Save "{data.infinitive}" to Word Bank
                        </button>
                        {data.conjugationGroup && (
                            <div className="vd-group-tag">{data.conjugationGroup}</div>
                        )}
                    </div>

                    <div className="vd-hint">Tap any form below to save it to your Word Bank</div>

                    <div className="vd-tense-list">
                        {tenseOrder.filter(t => data.tenses[t]).map(tenseKey => {
                            const isExpanded = expandedTense === tenseKey;
                            const forms = data.tenses[tenseKey];
                            return (
                                <div key={tenseKey} className={`vd-tense-card ${isExpanded ? 'expanded' : ''}`}>
                                    <div
                                        className="vd-tense-header"
                                        onClick={() => setExpandedTense(isExpanded ? null : tenseKey)}
                                    >
                                        <div className="vd-tense-name">{tenseLabels[tenseKey]}</div>
                                        <span className="vd-tense-chevron">{isExpanded ? '▲' : '▾'}</span>
                                    </div>
                                    {isExpanded && (
                                        <div className="vd-tense-body">
                                            {personOrder.filter(p => forms[p] !== undefined).map(person => {
                                                const form = forms[person];
                                                return (
                                                    <div
                                                        key={person}
                                                        className="vd-form-row"
                                                        onClick={() => setSaveTarget({ tense: tenseKey, person, form })}
                                                    >
                                                        <div className="vd-form-person">{personLabels[person] || person}</div>
                                                        <div className="vd-form-text">{form}</div>
                                                        <button
                                                            className="vd-form-speak-icon"
                                                            onClick={(e) => { e.stopPropagation(); speakText(form, null, targetLanguageRaw); }}
                                                            title="Hear pronunciation"
                                                        >
                                                            🔊
                                                        </button>
                                                        <span className="vd-form-save-icon">🔖</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <div style={{ height: '40px' }}></div>

            {saveTarget && (
                <div className="vd-modal-overlay" onClick={() => setSaveTarget(null)}>
                    <div className="vd-modal" onClick={e => e.stopPropagation()}>
                        <div className="vd-modal-title">Save to Word Bank</div>
                        <div className="vd-modal-desc">
                            Which would you like to save?
                        </div>
                        <div className="vd-modal-actions">
                            <button className="vd-modal-option" onClick={() => handleSave(false)}>
                                <strong>"{saveTarget.form}"</strong>
                                <span>{tenseLabels[saveTarget.tense]} · {personLabels[saveTarget.person] || saveTarget.person}</span>
                            </button>
                            <button className="vd-modal-option" onClick={() => handleSave(true)}>
                                <strong>"{data.infinitive}"</strong>
                                <span>the base verb</span>
                            </button>
                            <button className="vd-modal-cancel" onClick={() => setSaveTarget(null)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {savedFeedback && (
                <div className="vd-toast">✓ Saved "{savedFeedback}"!</div>
            )}

            <div className="vd-bottom-nav">
                <button className="vd-nav-item" onClick={() => navigate('/home')}>
                    <div className="vd-nav-icon">🏠</div>
                    <div className="vd-nav-label">Home</div>
                </button>
                <button className="vd-nav-item" onClick={() => navigate('/topics')}>
                    <div className="vd-nav-icon">📚</div>
                    <div className="vd-nav-label">Topics</div>
                </button>
                <button className="vd-nav-item active" onClick={() => navigate('/grammar')}>
                    <div className="vd-nav-icon nav-icon-grammar">📐</div>
                    <div className="vd-nav-label">Grammar</div>
                </button>
                <button className="vd-nav-item" onClick={() => navigate('/wordbank')}>
                    <div className="vd-nav-icon">📒</div>
                    <div className="vd-nav-label">Word bank</div>
                </button>
                <button className="vd-nav-item" onClick={() => navigate('/games')}>
                    <div className="vd-nav-icon">🎮</div>
                    <div className="vd-nav-label">Games</div>
                </button>
            </div>

        </div>
    );
}

export default VerbDetail;